// =============================================
// Admin Children Management Page
// Kipikid - מערכת נוכחות ילדים בגנים
// חיבור ל-API אמיתי
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  User,
  Building2,
  ChevronLeft,
  Users,
  ArrowRightLeft,
  Loader2,
  X,
  Tag,
} from 'lucide-react';
import { adminApi, gateApi, type AdminChildDto, type AdminKindergartenSearchDto, type ChildTagDto } from '../../services/api';
import { adminColors as colors } from '../../styles/colors';
import { useOrgFilter } from '../../hooks/useOrgFilter';

// חילוץ הודעת שגיאה בעברית מתשובת ה-API
const extractApiError = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || fallback;
};

export default function AdminChildren() {
  const { orgFilter } = useOrgFilter();
  const [children, setChildren] = useState<AdminChildDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChild, setSelectedChild] = useState<AdminChildDto | null>(null);
  const [transferChild, setTransferChild] = useState<AdminChildDto | null>(null);
  const [tagChild, setTagChild] = useState<AdminChildDto | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadChildren = async () => {
      setLoading(true);
      try {
        const result = await adminApi.getChildren(orgFilter);
        if (result.success && result.data) {
          setChildren(result.data);
        }
      } catch (err) {
        console.error('Failed to load children:', err);
      } finally {
        setLoading(false);
      }
    };
    loadChildren();
  }, [orgFilter, refreshKey]);

  const filteredChildren = children.filter(c => {
    const fullName = `${c.firstName} ${c.lastName}`;
    return fullName.includes(searchQuery) || c.kindergartenName.includes(searchQuery);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            ניהול ילדים
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {children.length} ילדים רשומים במערכת
          </p>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש לפי שם או גן..."
          style={{
            width: '100%',
            padding: '14px 44px 14px 14px',
            fontSize: '15px',
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
            direction: 'rtl',
          }}
        />
        <Search
          size={20}
          color={colors.textLight}
          style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>

      {/* Children List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען נתונים...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredChildren.map((child, index) => (
            <ChildCard
              key={child.publicId}
              child={child}
              index={index}
              onClick={() => setSelectedChild(child)}
              onTag={() => setTagChild(child)}
            />
          ))}
          {filteredChildren.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: 'white',
              borderRadius: '16px',
            }}>
              <p style={{ color: colors.textLight }}>לא נמצאו ילדים</p>
            </div>
          )}
        </div>
      )}

      {/* Child Details Modal */}
      <AnimatePresence>
        {selectedChild && (
          <ChildDetailsModal
            child={selectedChild}
            onClose={() => setSelectedChild(null)}
            onTransfer={(child) => {
              setSelectedChild(null);
              setTransferChild(child);
            }}
          />
        )}
      </AnimatePresence>

      {/* Child Tag Modal */}
      <AnimatePresence>
        {tagChild && (
          <ChildTagModal child={tagChild} onClose={() => setTagChild(null)} />
        )}
      </AnimatePresence>

      {/* Transfer Child Modal */}
      <AnimatePresence>
        {transferChild && (
          <TransferChildModal
            child={transferChild}
            onClose={() => setTransferChild(null)}
            onSuccess={() => {
              setTransferChild(null);
              setRefreshKey(k => k + 1);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Child Card Component
function ChildCard({ child, index, onClick, onTag }: { child: AdminChildDto; index: number; onClick: () => void; onTag: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      {/* Photo */}
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        overflow: 'hidden',
        backgroundColor: colors.secondaryLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {child.photoUrl ? (
          <img
            src={child.photoUrl}
            alt={child.firstName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <User size={24} color={colors.secondary} />
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>
          {child.firstName} {child.lastName}
        </h3>
        <p style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Building2 size={14} />
          {child.kindergartenName}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' }}>
          <span style={{ fontSize: '12px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={12} />
            {child.contactsCount} אנשי קשר
          </span>
          {child.gender && (
            <span style={{
              padding: '2px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              backgroundColor: child.gender === 1 ? '#dbeafe' : '#fce7f3',
              color: child.gender === 1 ? '#1d4ed8' : '#be185d',
            }}>
              {child.gender === 1 ? 'בן' : 'בת'}
            </span>
          )}
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onTag(); }}
        style={{
          padding: '8px 12px',
          borderRadius: '8px',
          border: 'none',
          backgroundColor: colors.primaryLight,
          color: colors.primary,
          fontSize: '13px',
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexShrink: 0,
        }}
      >
        <Tag size={14} />
        תג
      </button>

      <ChevronLeft size={20} color={colors.textLight} />
    </motion.div>
  );
}

// Child Details Modal
function ChildDetailsModal({ child, onClose, onTransfer }: { child: AdminChildDto; onClose: () => void; onTransfer: (child: AdminChildDto) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '400px',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: colors.secondaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {child.photoUrl ? (
              <img src={child.photoUrl} alt={child.firstName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <User size={28} color={colors.secondary} />
            )}
          </div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
              {child.firstName} {child.lastName}
            </h2>
            <p style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Building2 size={14} />
              {child.kindergartenName}
            </p>
          </div>
        </div>

        {/* Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: colors.background, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', color: colors.textLight }}>אנשי קשר</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{child.contactsCount}</span>
          </div>
          {child.gender && (
            <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: colors.background, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: colors.textLight }}>מין</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>{child.gender === 1 ? 'בן' : 'בת'}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <motion.button
            onClick={() => onTransfer(child)}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              backgroundColor: colors.primaryLight,
              color: colors.primary,
              border: 'none',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowRightLeft size={16} />
            העבר גן
          </motion.button>
          <motion.button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              backgroundColor: colors.background,
              color: colors.text,
              border: 'none',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            סגור
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Transfer Child Modal
function TransferChildModal({ child, onClose, onSuccess }: { child: AdminChildDto; onClose: () => void; onSuccess: () => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [kindergartens, setKindergartens] = useState<AdminKindergartenSearchDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [selectedKg, setSelectedKg] = useState<AdminKindergartenSearchDto | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    searchKindergartens('');
  }, []);

  const searchKindergartens = async (query: string) => {
    setLoading(true);
    try {
      const result = await adminApi.searchKindergartens(query);
      if (result.success && result.data) {
        // Filter out the child's current kindergarten
        setKindergartens(result.data.filter(k => k.publicId !== child.kindergartenPublicId));
      }
    } catch (err) {
      console.error('Failed to search kindergartens:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchKindergartens(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleTransfer = async () => {
    if (!selectedKg) return;
    setTransferring(true);
    setError('');
    try {
      const result = await adminApi.transferChild(child.publicId, selectedKg.publicId);
      if (result.success) {
        onSuccess();
      } else {
        setError(result.message || 'שגיאה בהעברת הילד');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'שגיאה בהעברת הילד');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            העברת ילד לגן אחר
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} color={colors.textLight} />
          </button>
        </div>

        {/* Child info */}
        <div style={{
          padding: '12px',
          borderRadius: '10px',
          backgroundColor: colors.background,
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: colors.secondaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <User size={20} color={colors.secondary} />
          </div>
          <div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              {child.firstName} {child.lastName}
            </p>
            <p style={{ fontSize: '12px', color: colors.textLight }}>
              גן נוכחי: {child.kindergartenName}
            </p>
          </div>
        </div>

        {/* Search kindergartens */}
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="חיפוש גן יעד..."
            style={{
              width: '100%',
              padding: '12px 40px 12px 12px',
              fontSize: '14px',
              border: `2px solid ${colors.border}`,
              borderRadius: '10px',
              outline: 'none',
              boxSizing: 'border-box',
              direction: 'rtl',
            }}
          />
          <Search
            size={18}
            color={colors.textLight}
            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
          />
        </div>

        {/* Kindergarten list */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          minHeight: '120px',
          maxHeight: '250px',
          marginBottom: '16px',
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : kindergartens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight, fontSize: '13px' }}>
              לא נמצאו גנים
            </div>
          ) : (
            kindergartens.map(kg => (
              <div
                key={kg.publicId}
                onClick={() => setSelectedKg(kg)}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: selectedKg?.publicId === kg.publicId ? colors.primaryLight : 'white',
                  border: `1px solid ${selectedKg?.publicId === kg.publicId ? colors.primary : colors.border}`,
                  transition: 'all 0.15s',
                }}
              >
                <p style={{
                  fontSize: '14px',
                  fontWeight: selectedKg?.publicId === kg.publicId ? '600' : '500',
                  color: selectedKg?.publicId === kg.publicId ? colors.primary : colors.text,
                  marginBottom: '2px',
                }}>
                  {kg.name}
                </p>
                <p style={{ fontSize: '12px', color: colors.textLight }}>
                  {kg.city}, {kg.street} {kg.houseNumber || ''}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Error */}
        {error && (
          <p style={{ fontSize: '13px', color: colors.error, marginBottom: '12px', textAlign: 'center' }}>
            {error}
          </p>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <motion.button
            onClick={onClose}
            disabled={transferring}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: 'white',
              color: colors.text,
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
          >
            ביטול
          </motion.button>
          <motion.button
            onClick={handleTransfer}
            disabled={!selectedKg || transferring}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: !selectedKg || transferring ? '#c7d2fe' : colors.primary,
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: !selectedKg || transferring ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            whileTap={{ scale: !selectedKg || transferring ? 1 : 0.98 }}
          >
            {transferring && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {transferring ? 'מעביר...' : 'העבר'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Child Tag Modal - שיוך / החלפה / הסרת תג BLE
const formatTagDate = (iso?: string | null): string => {
  if (!iso) return 'מעולם';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'מעולם';
  return d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ChildTagModal({ child, onClose }: { child: AdminChildDto; onClose: () => void }) {
  const [currentTag, setCurrentTag] = useState<ChildTagDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tagIdentifier, setTagIdentifier] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadTag = async () => {
      setLoading(true);
      try {
        const result = await gateApi.getChildTags(child.publicId);
        if (result.success && result.data) {
          const active = result.data.find(t => t.isActive) || null;
          setCurrentTag(active);
          setNotes(active?.notes || '');
        }
      } catch (err) {
        setError(extractApiError(err, 'שגיאה בטעינת התג'));
      } finally {
        setLoading(false);
      }
    };
    loadTag();
  }, [child.publicId, refreshKey]);

  // מזהה תקין: MAC (12 תווי HEX עם או בלי נקודתיים), או מזהה iBeacon בפורמט IB:uuid:major:minor
  const trimmedId = tagIdentifier.trim();
  const isIBeaconId = /^IB:[0-9a-fA-F-]{36}:\d{1,5}:\d{1,5}$/i.test(trimmedId);
  const normalizedMac = tagIdentifier.replace(/[:\-\s]/g, '');
  const macValid = isIBeaconId || /^[0-9a-fA-F]{12}$/.test(normalizedMac);

  const handleAssign = async () => {
    if (!macValid) {
      setError('מזהה תג חייב להיות MAC של 12 תווים הקסדצימליים (למשל AA:BB:CC:DD:EE:FF) או IB:uuid:major:minor');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await gateApi.assignChildTag(child.publicId, {
        tagIdentifier: isIBeaconId ? trimmedId.toUpperCase() : normalizedMac.toUpperCase(),
        notes: notes.trim() || undefined,
      });
      if (result.success) {
        setTagIdentifier('');
        setRefreshKey(k => k + 1);
      } else {
        setError(result.message || 'שגיאה בשיוך התג');
      }
    } catch (err) {
      setError(extractApiError(err, 'שגיאה בשיוך התג'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await gateApi.removeChildTag(child.publicId);
      if (result.success) {
        setCurrentTag(null);
        setNotes('');
        setRefreshKey(k => k + 1);
      } else {
        setError(result.message || 'שגיאה בהסרת התג');
      }
    } catch (err) {
      setError(extractApiError(err, 'שגיאה בהסרת התג'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        dir="rtl"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            תג נוכחות - {child.firstName} {child.lastName}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={20} color={colors.textLight} />
          </button>
        </div>

        {/* תג נוכחי */}
        <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: colors.background, marginBottom: '16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textLight }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : currentTag ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: colors.text }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textLight }}>מזהה תג</span>
                <span dir="ltr" style={{ fontWeight: 600, fontFamily: 'monospace' }}>{currentTag.tagIdentifier}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textLight }}>נראה לאחרונה</span>
                <span>{formatTagDate(currentTag.lastSeenAt)}</span>
              </div>
              {currentTag.batteryPercent != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textLight }}>סוללה</span>
                  <span style={{ fontWeight: 600, color: currentTag.batteryPercent <= 20 ? colors.error : colors.text }}>
                    {currentTag.batteryPercent}%
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p style={{ fontSize: '13px', color: colors.textLight, textAlign: 'center' }}>לא משויך תג לילד</p>
          )}
        </div>

        {/* שיוך / החלפה */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.text, marginBottom: '6px' }}>
              {currentTag ? 'כתובת MAC של תג חדש (להחלפה)' : 'כתובת MAC של התג'}
            </label>
            <input
              value={tagIdentifier}
              onChange={(e) => setTagIdentifier(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF"
              dir="ltr"
              style={{
                width: '100%', padding: '12px', fontSize: '14px', border: `2px solid ${colors.border}`,
                borderRadius: '10px', outline: 'none', boxSizing: 'border-box', direction: 'ltr', fontFamily: 'monospace',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.text, marginBottom: '6px' }}>
              הערות
            </label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="הערות (אופציונלי)"
              maxLength={200}
              style={{
                width: '100%', padding: '12px', fontSize: '14px', border: `2px solid ${colors.border}`,
                borderRadius: '10px', outline: 'none', boxSizing: 'border-box', direction: 'rtl',
              }}
            />
          </div>
        </div>

        {error && (
          <p style={{ fontSize: '13px', color: colors.error, marginBottom: '12px', textAlign: 'center' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          {currentTag && (
            <motion.button
              onClick={handleRemove}
              disabled={saving}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#fee2e2',
                color: colors.error, fontSize: '14px', fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              הסר תג
            </motion.button>
          )}
          <motion.button
            onClick={handleAssign}
            disabled={saving || !tagIdentifier.trim()}
            whileTap={{ scale: 0.98 }}
            style={{
              flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
              backgroundColor: saving || !tagIdentifier.trim() ? '#c7d2fe' : colors.primary,
              color: 'white', fontSize: '14px', fontWeight: 600,
              cursor: saving || !tagIdentifier.trim() ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {currentTag ? 'החלף תג' : 'שייך תג'}
          </motion.button>
          <motion.button
            onClick={onClose}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: '12px', borderRadius: '10px', border: `1px solid ${colors.border}`,
              backgroundColor: 'white', color: colors.text, fontSize: '14px', fontWeight: 500, cursor: 'pointer',
            }}
          >
            סגור
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
